// The patching policy editor's plain functions (no React): what to ask cma/softwarepolicy for, how a person's form becomes the document
// the API stores (and back), and the checks that save a round trip. The API checks every document again.
//
// A patch policy has a filter (label is value, all must match, like a schedule's) and the patching limits for the machines that match.
// Account * is the global standard; a customer's own is an override. For each limit the customer's policy wins over the global one, then
// the one with more filters (the API decides; see routes/cma/patchdrift/_compare.py). A limit left blank is not set by this policy, so
// it comes from the next one down.

import { GLOBAL, POLICY_URL, filterText } from '../../pages/SoftwarePolicy/policyEdit';

export const patchPoliciesUrl = (account) => `${POLICY_URL}?${new URLSearchParams({ account, kind: 'patchpolicy' }).toString()}`;

const list = (v) => (Array.isArray(v) ? v : []);

// The limits, in the order the form shows them. kind 'flag' is 0 or 1; the others are whole numbers. off = what 0 means.
export const LIMIT_FIELDS = [
  { key: 'maxDaysSinceInstall', label: 'Last Windows update no older than (days)', max: 3650, short: (n) => `Update ${n} d`, help: 'Not patched when the newest Windows update is older.' },
  { key: 'maxDaysSinceSecurity', label: 'Last security update no older than (days)', max: 3650, short: (n) => `Security ${n} d`, help: 'Security update overdue when the newest cumulative or security update is older.' },
  { key: 'maxPendingRestartDays', label: 'A restart may wait (days)', max: 3650, short: (n) => `Restart ${n} d`, help: 'Restart needed when one has been waiting longer.' },
  { key: 'maxFailures', label: 'Failed updates allowed', max: 3650, short: (n) => `${n} failures`, help: '0 means any failed update is a finding.' },
  { key: 'minFeatureBuild', label: 'Minimum Windows build', max: 99999, short: (n) => (n > 0 ? `Build ${n}+` : 'No minimum build'), help: 'The number before the dot, for example 26100 (Windows 11 24H2) or 20348 (Server 2022). 0 = no minimum.', labelKey: 'minFeatureLabel' },
  { key: 'maxScanAgeDays', label: 'Windows must have looked for updates within (days)', max: 3650, short: (n) => (n > 0 ? `Scan ${n} d` : 'Scan not checked'), help: '0 = not checked.' },
  { key: 'maxReportAgeDays', label: 'The machine must have reported within (days)', max: 3650, short: (n) => (n > 0 ? `Report ${n} d` : 'Report not checked'), help: 'Shows the machine as not reporting. 0 = not checked.' },
  { key: 'maxQualityDeferDays', label: 'Quality updates may be deferred up to (days)', max: 3650, short: (n) => (n > 0 ? `Defer ${n} d` : 'Deferral not checked'), help: '1 flags any deferral. 0 = not checked.' },
  { key: 'forbidPause', label: 'Updates may not be paused', flag: true, short: (n) => (n >= 1 ? 'No pausing' : 'Pause not checked'), help: 'A machine with updates paused is a finding.' },
  { key: 'forbidPinnedRelease', label: 'The Windows release may not be pinned', flag: true, short: (n) => (n >= 1 ? 'No pinning' : 'Pin not checked'), help: 'A machine pinned to a feature release by policy is a finding.' },
];
export const COMMON_LABELS = ['devicetype', 'account', 'device_name', 'software'];

// ---- reading what the API returned ----------------------------------------------------------------------------------------

export const isGlobal = (p) => p?.scope === 'global' || p?.account === GLOBAL;

// The limits a policy sets, as short chips for the table: "Update 35 d", "Build 26100+".
export const limitChips = (patch) => LIMIT_FIELDS.filter((f) => patch && typeof patch[f.key] === 'number').map((f) => f.short(patch[f.key]));

// An exception is over the day after it ends. today: 'yyyy-MM-dd' (the caller's clock, so tests can fix it).
export const exceptionState = (exception, today) => {
  if (!exception) return null;
  return String(exception.expires) < today ? 'expired' : 'active';
};
export const todayText = (now = new Date()) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

// Global ones first, then the customer's; within each, fewer filters first (the broader one), then by filter.
export const ordered = (policies) => [...list(policies)].sort((a, b) => (isGlobal(a) ? 0 : 1) - (isGlobal(b) ? 0 : 1)
  || list(a.filter).length - list(b.filter).length || filterText(a.filter).localeCompare(filterText(b.filter)));

// ---- the form -------------------------------------------------------------------------------------------------------------

// The form for a new policy of this account (GLOBAL for the standard) or for an existing one.
export const emptyForm = (account) => ({
  description: '',
  filter: account === GLOBAL ? [{ label: 'devicetype', value: 'workstation' }] : [{ label: 'account', value: account }],
  limits: {},
  featureLabel: '',
  isException: false,
  exception: { reason: '', approvedBy: '', expires: '' },
  enabled: true,
});

export const formOf = (policy) => {
  const patch = policy.patch || {};
  const limits = {};
  LIMIT_FIELDS.forEach((f) => { if (typeof patch[f.key] === 'number') limits[f.key] = String(patch[f.key]); });
  return {
    description: policy.description || '',
    filter: list(policy.filter).map((f) => ({ label: f.label, value: f.value })),
    limits,
    featureLabel: patch.minFeatureLabel || '',
    isException: Boolean(policy.exception),
    exception: { reason: policy.exception?.reason || '', approvedBy: policy.exception?.approvedBy || '', expires: policy.exception?.expires || '' },
    enabled: policy.enabled !== false,
  };
};

const wholeNumber = (text) => /^\d+$/.test(String(text).trim());

// What is wrong with the form, in plain words, or '' when it can be saved.
export const problemWith = (form, account) => {
  const filter = list(form.filter).filter((f) => f.label.trim() || f.value.trim());
  if (filter.length === 0) return 'Say which machines this applies to: at least one label and value.';
  if (filter.some((f) => !f.label.trim() || !f.value.trim())) return 'Each filter needs both a label and a value.';
  if (filter.length > 10) return 'At most 10 filters.';
  const labels = filter.map((f) => f.label.trim().toLowerCase());
  if (new Set(labels).size !== labels.length) return 'A label can only be used once in a filter: a machine cannot have two values of one label.';
  if (account === GLOBAL && labels.includes('account')) return 'The global standard cannot name an account: that is a customer override.';
  const set = LIMIT_FIELDS.filter((f) => String(form.limits[f.key] ?? '').trim() !== '');
  if (set.length === 0) return 'Set at least one limit.';
  const bad = set.find((f) => !wholeNumber(form.limits[f.key]) || Number(form.limits[f.key]) > (f.flag ? 1 : f.max));
  if (bad) return `${bad.label}: a whole number from 0 to ${bad.flag ? 1 : bad.max}.`;
  if (String(form.featureLabel).length > 40) return 'The Windows release name is at most 40 characters.';
  if (form.isException) {
    const e = form.exception;
    if (!e.reason.trim() || !e.approvedBy.trim()) return 'An exception needs a reason and who approved it.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(e.expires)) return 'An exception needs an end date: one with no end is a gap, not an exception.';
  }
  return '';
};

// The document to POST.
export const bodyOf = (form, account) => {
  const patch = {};
  LIMIT_FIELDS.forEach((f) => {
    const text = String(form.limits[f.key] ?? '').trim();
    if (text !== '') patch[f.key] = Number(text);
  });
  if (typeof patch.minFeatureBuild === 'number' && form.featureLabel.trim()) patch.minFeatureLabel = form.featureLabel.trim();
  const body = {
    kind: 'patchpolicy',
    account,
    description: form.description.trim(),
    filter: form.filter.filter((f) => f.label.trim() && f.value.trim()).map((f) => ({ label: f.label.trim(), value: f.value.trim() })),
    patch,
    enabled: form.enabled,
  };
  if (form.isException && account !== GLOBAL) {
    body.exception = { reason: form.exception.reason.trim(), approvedBy: form.exception.approvedBy.trim(), expires: form.exception.expires };
  }
  return body;
};
